import { z } from 'zod';

import { IdSchema, IsoDateSchema } from './common.js';
import { DietArchetypeSampleDayV1Schema } from './diet-archetype.js';

// ─── DietRecommendation (one candidate, post server-enrichment) ───────────

// Mirrors RoutineRecommendationV1Schema. Suitability is server-clamped to
// [50,99]; no two candidates in a batch may share the same score (Layer B
// nudges duplicates by ±1, same convention as routine recs).
/** Single AI-generated diet recommendation card. */
export const DietRecommendationV1Schema = z.object({
  version: z.literal('v1'),
  id: IdSchema,
  archetypeId: z.string().min(1).max(80),
  archetypeName: z.string().min(1).max(120),
  suitabilityPct: z.number().int().min(50).max(99),
  rationale: z.string().min(20).max(500),
  sampleDayPreview: DietArchetypeSampleDayV1Schema,
});
export type DietRecommendationV1 = z.infer<typeof DietRecommendationV1Schema>;

// ─── DietRecommendationBatch (DB row + response shape) ────────────────────

/** Batch of exactly 3 diet recommendation candidates, persisted per /recommend call. */
export const DietRecommendationBatchV1Schema = z.object({
  version: z.literal('v1'),
  id: IdSchema,
  userId: IdSchema,
  candidates: z.array(DietRecommendationV1Schema).length(3),
  selectedArchetypeId: z.string().nullable(),
  regenCount: z.number().int().min(0).max(1),
  canRegen: z.boolean(),
  modelUsed: z.string().min(1),
  promptVersion: z.string().min(1),
  createdAt: IsoDateSchema,
});
export type DietRecommendationBatchV1 = z.infer<typeof DietRecommendationBatchV1Schema>;

// ─── API request / response DTOs ──────────────────────────────────────────

/** POST /v1/diet/recommend — returns a fresh 3-card batch. */
export const RecommendDietsResponseSchema = z.object({
  batch: DietRecommendationBatchV1Schema,
});
export type RecommendDietsResponse = z.infer<typeof RecommendDietsResponseSchema>;

/** GET /v1/diet/recommendations — null when user has never called /recommend. */
export const GetDietRecommendationsResponseSchema = z.object({
  batch: DietRecommendationBatchV1Schema.nullable(),
});
export type GetDietRecommendationsResponse = z.infer<typeof GetDietRecommendationsResponseSchema>;

/** POST /v1/diet/recommendations/select — user picks an archetype to materialize. */
export const SelectDietArchetypeRequestSchema = z.object({
  archetypeId: z.string().min(1).max(80),
});
export type SelectDietArchetypeRequest = z.infer<typeof SelectDietArchetypeRequestSchema>;

/** Returns the new Plan row id (server has materialized the diet plan). */
export const SelectDietArchetypeResponseSchema = z.object({
  planId: IdSchema,
});
export type SelectDietArchetypeResponse = z.infer<typeof SelectDietArchetypeResponseSchema>;
